import { makeEsArticle } from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

const dipRelated: RL[] = [
  {
    title: "¿Es seguro SAINT® TMS?",
    href: "/es/learn/is-saint-tms-safe",
    description:
      "Efectos secundarios, efectos a largo plazo y quién no debe recibir SAINT®",
  },
  {
    title: "¿Qué pasa después de SAINT® TMS?",
    href: "/es/learn/what-happens-after-saint-tms",
    description:
      "Recuperación, seguimiento y qué esperar después de la semana de tratamiento",
  },
  {
    title: "¿Y si SAINT® no funciona?",
    href: "/es/learn/what-if-saint-doesnt-work",
    description: "Próximos pasos si SAINT® no produce alivio suficiente",
  },
  {
    title: "¿Qué se siente con SAINT® TMS?",
    href: "/es/learn/what-does-saint-tms-feel-like",
    description: "Qué se siente la estimulación durante una sesión",
  },
  {
    title: "SAINT® TMS acelerada",
    href: "/es/saint",
    description: "El protocolo de 5 días guiado por fMRI que ofrecemos",
  },
  {
    title: "¿Qué es la depresión resistente al tratamiento?",
    href: "/es/learn/what-is-treatment-resistant-depression",
    description: "Definición, causas y opciones de tratamiento",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "¿Es normal sentirse peor durante la TMS?",
    answer:
      "Algunas personas notan una caída temporal del estado de ánimo durante o poco después del tratamiento con TMS. A menudo se llama 'caída por TMS'. No es lo mismo que el tratamiento no funcione, y suele pasar en días. El ensayo aleatorizado de SAINT® de 2022 no reportó empeoramiento como un resultado común (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar. Si su ánimo baja, avise al equipo de tratamiento de inmediato.",
  },
  {
    question: "¿Cuánto dura la caída por TMS?",
    answer:
      "Una caída por TMS, cuando ocurre, suele durar unos días. Puede aparecer durante la semana de tratamiento o en la primera o segunda semana después de terminar. La mayoría de las personas que la experimentan encuentra que se resuelve sola a medida que el cerebro se ajusta. Si un ánimo bajo dura más de un par de semanas o empeora, contacte a su equipo de tratamiento. Si está en crisis, llame o envíe un texto al 988.",
  },
  {
    question: "¿La TMS puede empeorar la ansiedad?",
    answer:
      "Algunas personas reportan sentirse más ansiosas durante el tratamiento con TMS, aunque no es un efecto secundario bien establecido en los datos del ensayo. SAINT® está autorizado por la FDA para el trastorno depresivo mayor, no para los trastornos de ansiedad. Si nota más ansiedad durante el tratamiento, avise al equipo para que ajuste el plan. Cualquier cambio de ánimo, incluida la ansiedad, debe reportarse y monitorearse.",
  },
  {
    question: "¿Una caída significa que SAINT® no está funcionando?",
    answer:
      "No necesariamente. Una caída temporal no significa que el tratamiento haya fracasado. Algunas personas que se sienten peor brevemente luego mejoran. El equipo de tratamiento monitorea su ánimo durante toda la semana y después, así puede distinguir entre una caída pasajera y un patrón que requiere otro enfoque. Si le preocupa, llámenos al 954-827-9793.",
  },
  {
    question: "¿Qué debo hacer si me siento peor después de SAINT®?",
    answer:
      "Llame a su equipo de tratamiento de inmediato. No espere a su próxima visita de seguimiento. El equipo puede revisar sus síntomas, ajustar el plan o traer una evaluación psiquiátrica. Si tiene pensamientos de suicidio o está en crisis, llame o envíe un texto al 988 de inmediato. La Línea de Vida para la Prevención del Suicidio y Crisis 988 está disponible 24/7, gratis y confidencial.",
  },
];

export const esCanSaintMakeDepressionWorse = makeEsArticle({
  slug: "can-saint-make-depression-worse",
  title:
    "¿Puede SAINT® TMS empeorar la depresión? La 'caída por TMS' explicada",
  description:
    "Algunas personas se sienten peor brevemente durante o después de la TMS. Qué es la 'caída por TMS', qué tan común es con protocolos acelerados y qué hacer.",
  h1: "¿Puede SAINT® empeorar la depresión? Entender la 'caída por TMS'",
  breadcrumbLabel: "¿Puede SAINT® empeorar la depresión?",
  shortAnswer:
    "Algunas personas notan una caída temporal del ánimo durante o poco después del tratamiento con TMS, a menudo llamada 'caída por TMS'. Suele ser leve y pasar en días. El ensayo aleatorizado de SAINT® de 2022 no reportó empeoramiento como un resultado común (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar. Como en cualquier tratamiento de la depresión, existe un riesgo de empeoramiento de los síntomas o pensamientos suicidas. Si está en crisis, llame o envíe un texto al 988.",
  keyTakeaways: [
    "Una 'caída por TMS' es un empeoramiento temporal, generalmente leve, del ánimo durante o poco después del tratamiento.",
    "El ensayo de SAINT® de 2022 no reportó empeoramiento como resultado común (Cole et al., 2022). Los resultados individuales pueden variar.",
    "Una caída no significa que el tratamiento haya fracasado: la mayoría de quienes la experimentan mejoran.",
    "Avise al equipo de tratamiento de inmediato si su ánimo baja; pueden ajustar el plan.",
    "SAINT® está autorizado por la FDA para el trastorno depresivo mayor en adultos, no para ansiedad, TOC, TEPT ni trastorno bipolar.",
    "Si está en crisis, llame o envíe un texto al 988.",
  ],
  sections: [
    {
      heading: "Respuesta rápida: ¿puede SAINT® empeorar la depresión?",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) está autorizado por la FDA para el trastorno depresivo mayor (TDM) en adultos cuya depresión no ha mejorado lo suficiente con medicación antidepresiva previa. Algunas personas notan una caída temporal del ánimo durante o poco después del tratamiento, a menudo llamada 'caída por TMS'. Suele ser leve y pasar en días. El ensayo aleatorizado de SAINT® de 2022 no reportó empeoramiento de la depresión como resultado común (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar.",
        "Como en cualquier tratamiento de la depresión, existe un riesgo de empeoramiento de los síntomas o pensamientos suicidas. El equipo de tratamiento lo monitorea durante toda la semana y después. Si nota que su ánimo empeora, avise de inmediato. Si está en crisis, llame o envíe un texto al 988. La Línea de Vida para la Prevención del Suicidio y Crisis 988 está disponible 24/7, gratis y confidencial.",
        "Si está en crisis o tiene pensamientos de suicidio, llame o envíe un texto al 988 ahora mismo. La Línea de Vida para la Prevención del Suicidio y Crisis 988 está disponible 24/7.",
      ],
    },
    {
      heading: "Qué es la 'caída por TMS'",
      paragraphs: [
        "La 'caída por TMS' es un término que pacientes y médicos usan para describir un empeoramiento temporal del ánimo que algunas personas experimentan durante o poco después del tratamiento con TMS. No es un diagnóstico médico oficial y no es lo mismo que el tratamiento no funcione. Piense en ello como un período de ajuste: el cerebro responde a la estimulación repetida y algunas personas se sienten peor antes de mejorar.",
        "La caída puede manifestarse como una bajada del ánimo, más facilidad para llorar, irritabilidad, fatiga o la sensación de que las cosas no mejoran lo bastante rápido. Suele ser leve. Algunas personas no la notan en absoluto. Otras la sienten uno o dos días durante la semana de tratamiento o en la primera o segunda semana después de terminar.",
        "Es importante distinguir una caída de un empeoramiento real que requiere atención. Una caída es temporal y leve. Si su ánimo baja de forma pronunciada, dura más de un par de semanas o incluye pensamientos de suicidio, eso no es una caída: llame a su equipo de tratamiento y, si está en crisis, llame o envíe un texto al 988.",
        "Conozca más sobre [[cómo funciona SAINT®|/es/saint]] o [[qué es la depresión resistente al tratamiento|/es/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "Lo que reportaron los ensayos",
      paragraphs: [
        "En el ensayo aleatorizado de SAINT® de 2022, los investigadores rastrearon eventos adversos y cambios de síntomas durante todo el tratamiento y el seguimiento. El empeoramiento de la depresión no se reportó como resultado común. Los efectos secundarios más reportados fueron cefalea y molestia en el cuero cabelludo, leves a moderados y temporales (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar.",
        "El ensayo usó escalas de ánimo estandarizadas para rastrear los síntomas en múltiples puntos durante la semana de tratamiento de cinco días y durante el seguimiento. Esto significa que el equipo de investigación monitoreaba activamente el empeoramiento y no lo señaló como un problema frecuente en este estudio.",
        "Dicho esto, los ensayos publicados de SAINT® son relativamente pequeños en comparación con los grandes ensayos multicéntricos de medicación. Los efectos raros pueden no aparecer hasta que se trate a más personas durante períodos más largos. Su psiquiatra puede explicar qué cubre y qué no la evidencia actual para su situación individual.",
        "Para un desglose completo de efectos secundarios y riesgos, vea [[¿Es seguro SAINT® TMS?|/es/learn/is-saint-tms-safe]].",
      ],
    },
    {
      heading:
        "Día a día: qué es normal en la semana de 5 días y los 30 días después",
      paragraphs: [
        "Durante la semana de SAINT® de cinco días, recibe 10 sesiones al día con descansos entre sesiones. Es normal sentirse cansado: pasa la mayor parte del día en la clínica. La cefalea y la molestia en el cuero cabelludo son los efectos físicos más comunes y suelen disminuir en el primer o segundo día.",
        "En cuanto al ánimo, algunas personas mejoran rápido, incluso en el primer o segundo día. Otras no notan un cambio hasta más adelante en la semana o después de que termine. Una caída temporal del ánimo, si ocurre, es más probable que aparezca durante la semana o en la primera o segunda semana después del tratamiento.",
        "En los 30 días después de SAINT®, el equipo de tratamiento hace seguimiento para rastrear su respuesta. Algunas personas siguen mejorando. Otras se estabilizan. Un número pequeño puede necesitar tratamiento adicional o un enfoque distinto. El equipo se comunica con usted para distinguir entre una caída pasajera, una meseta y la necesidad de ajustar el plan.",
        "Si en algún momento su ánimo baja de forma pronunciada, se siente sin esperanza o tiene pensamientos de suicidio, llame a su equipo de tratamiento de inmediato. Si está en crisis, llame o envíe un texto al 988. No espere a su próxima visita de seguimiento.",
      ],
    },
    {
      heading: "Cuándo llamarnos — y al 988",
      paragraphs: [
        "Llame a su equipo de tratamiento al 954-827-9793 si nota cualquiera de lo siguiente durante o después de SAINT®:",
        "Una caída pronunciada del ánimo que no se levanta en uno o dos días. Pensamientos de suicidio o autolesión. Sentirse sin esperanza o que las cosas nunca van a mejorar. Síntomas nuevos que no tenía antes del tratamiento. Ansiedad nueva o significativamente peor. Cualquier cambio que le preocupe.",
        "Si está en crisis o tiene pensamientos de suicidio, llame o envíe un texto al 988 de inmediato. La Línea de Vida para la Prevención del Suicidio y Crisis 988 está disponible 24/7, gratis y confidencial. También puede enviar un texto al 988. Si está en peligro inmediato, llame al 911.",
        "No necesita averiguar si lo que siente es una 'caída' o algo más serio. Llame y deje que el equipo clínico lo evalúe. Para eso sirve el seguimiento. [[Verifique su elegibilidad|/am-i-a-candidate-screener]] o [[verifique su seguro|/check-insurance-options]] si está considerando SAINT®.",
      ],
    },
    {
      heading: "La caída por TMS: qué es normal y cuándo llamar",
      table: {
        headers: [
          "Período",
          "Lo que algunas personas notan",
          "Lo típico",
          "Cuándo llamar",
        ],
        rows: [
          [
            "Días 1–2 del tratamiento",
            "Cefalea, molestia en el cuero cabelludo, fatiga; posible caída leve del ánimo",
            "Los efectos físicos disminuyen; el ánimo puede fluctuar",
            "Llame si la cefalea es intensa o el ánimo baja de forma pronunciada",
          ],
          [
            "Días 3–5 del tratamiento",
            "Cansancio por el calendario; algunos notan mejoría temprana del ánimo",
            "La mayoría tolera bien la semana; el ánimo puede empezar a mejorar",
            "Llame si el ánimo empeora o aparecen síntomas nuevos",
          ],
          [
            "Primera semana después del tratamiento",
            "Posible 'caída por TMS': caída temporal y leve del ánimo",
            "La caída suele pasar en días; se programa seguimiento",
            "Llame si la caída dura más de unos días o empeora",
          ],
          [
            "Semanas 2–4 después del tratamiento",
            "El ánimo se estabiliza; algunos siguen mejorando",
            "Las visitas de seguimiento rastrean la respuesta; el equipo ajusta si es necesario",
            "Llame si el ánimo baja o si surgen pensamientos de suicidio",
          ],
          [
            "En cualquier momento",
            "Pensamientos de suicidio, autolesión o desesperanza",
            "No es una caída: necesita atención inmediata",
            "Llame o envíe un texto al 988 de inmediato; llame al 911 si hay peligro",
          ],
        ],
      },
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: dipRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "octubre de 2026",
    lastReviewed: "octubre de 2026",
    lastUpdated: "octubre de 2026",
  },
  toc: [
    {
      label: "Respuesta rápida",
      href: "#respuesta-rapida-puede-saint-empeorar-la-depresion",
    },
    {
      label: "Qué es la 'caída por TMS'",
      href: "#que-es-la-caida-por-tms",
    },
    {
      label: "Lo que reportaron los ensayos",
      href: "#lo-que-reportaron-los-ensayos",
    },
    {
      label: "Día a día: semana de 5 días y 30 días después",
      href: "#dia-a-dia-que-es-normal-en-la-semana-de-5-dias-y-los-30-dias-despues",
    },
    {
      label: "Cuándo llamarnos — y al 988",
      href: "#cuando-llamarnos-y-al-988",
    },
    {
      label: "Tabla de la caída por TMS",
      href: "#la-caida-por-tms-que-es-normal-y-cuando-llamar",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® no es para todos. Está contraindicado con metal magnético no removible cerca de la cabeza, ciertos dispositivos implantados o presión intracraneal elevada. Posibles efectos: cefalea, fatiga, molestia en el cuero cabelludo y ansiedad; la TMS conlleva un riesgo bajo de convulsión y, como en cualquier tratamiento de la depresión, riesgo de empeoramiento o pensamientos suicidas. Si está en crisis, llame o envíe un texto al 988.",
  trademarkFooter:
    "SAINT® es una marca registrada de Stanford University, licenciada en exclusiva a Magnus Medical, Inc. NWCOA no está afiliado a Stanford University.",
  ctaBand: {
    headline: "Vea si SAINT® es una opción, sin costo",
    primaryText: "Evaluar mi candidatura",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Verificar mi seguro",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
